import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = {
    bank_name: "",
    holder_name: "",
    account_type: "Savings",
    last_4: "",
    ifsc: "",
    upi_id: "",
    used_for_auto_debit: false,
    remarks: "",
};

export default function BankAccounts() {
    const { hasRole } = useAuth();
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = () => api.get("/bank-accounts").then((r) => setRows(r.data));
    useEffect(() => { load(); }, []);
    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

    const del = async (id, label) => {
        if (!window.confirm(`Delete bank account ${label}? This cannot be undone.`)) return;
        try {
            await api.delete(`/bank-accounts/${id}`);
            toast.success("Bank account deleted");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await api.patch(`/bank-accounts/${editingId}`, f);
                toast.success("Bank account updated");
            } else {
                await api.post("/bank-accounts", f);
                toast.success("Bank account added");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (row) => {
        setEditingId(row.id);
        setF({
            bank_name: row.bank_name || "",
            holder_name: row.holder_name || "",
            account_type: row.account_type || "Savings",
            last_4: row.last_4 || "",
            ifsc: row.ifsc || "",
            upi_id: row.upi_id || "",
            used_for_auto_debit: !!row.used_for_auto_debit,
            remarks: row.remarks || "",
        });
        setOpen(true);
    };

    return (
        <div data-testid="bank-accounts-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Bank Accounts</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Payment sources.</h1>
                    <p className="text-sm text-stone-500 mt-3">Accounts used for auto-debit and card payments.</p>
                </div>
                {hasRole("admin", "finance") && (
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-bank-btn" onClick={openAdd}><Plus size={16} className="mr-1.5" /> Add bank account</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit bank account" : "New bank account"}</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="eyebrow">Bank Name</Label><Input required value={f.bank_name} onChange={setInput("bank_name")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Account Holder</Label><Input required value={f.holder_name} onChange={setInput("holder_name")} className="mt-1.5" /></div>
                                    <div>
                                        <Label className="eyebrow">Account Type</Label>
                                        <Select value={f.account_type} onValueChange={set("account_type")}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Savings">Savings</SelectItem>
                                                <SelectItem value="Current">Current</SelectItem>
                                                <SelectItem value="OD">Overdraft</SelectItem>
                                                <SelectItem value="Other">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="eyebrow">Last 4 Digits</Label><Input required maxLength={4} value={f.last_4} onChange={setInput("last_4")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">IFSC</Label><Input value={f.ifsc} onChange={setInput("ifsc")} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">UPI ID</Label><Input value={f.upi_id} onChange={setInput("upi_id")} className="mt-1.5" /></div>
                                    <div className="col-span-2 flex items-center justify-between border border-stone-200 rounded-md px-3 py-2.5">
                                        <div className="text-sm">Used for Auto Debit</div>
                                        <Switch checked={f.used_for_auto_debit} onCheckedChange={set("used_for_auto_debit")} />
                                    </div>
                                </div>
                                <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">{editingId ? "Save changes" : "Add account"}</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Bank</th><th>Holder</th><th>Type</th><th>Last 4</th><th>IFSC</th><th>UPI</th><th>Auto Debit</th><th></th></tr>
                        </thead>
                        <tbody>
                            {rows.map((b) => (
                                <tr key={b.id}>
                                    <td className="font-medium">{b.bank_name}</td>
                                    <td>{b.holder_name}</td>
                                    <td>{b.account_type}</td>
                                    <td className="font-mono-figure">•••• {b.last_4}</td>
                                    <td className="font-mono-figure">{b.ifsc || "—"}</td>
                                    <td className="font-mono-figure">{b.upi_id || "—"}</td>
                                    <td>{b.used_for_auto_debit ? "Yes" : "No"}</td>
                                    <td className="text-right">
                                        {hasRole("admin", "finance") && (
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    onClick={() => openEdit(b)}
                                                    title="Edit account"
                                                    className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                                    data-testid={`bank-edit-${b.id}`}
                                                >
                                                    <Pencil size={13} />
                                                </button>
                                                <button
                                                    onClick={() => del(b.id, `${b.bank_name} •••• ${b.last_4}`)}
                                                    title="Delete account"
                                                    className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600"
                                                    data-testid={`bank-delete-${b.id}`}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-stone-500 py-14">No bank accounts yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
